import { t } from '@lingui/core/macro'
import { Trans } from '@lingui/react/macro'
import { Popover } from 'antd'
import CantileverButton from 'components/CantileverButton'
import {
  ChartControls,
  useOperationalEvents,
  useScatterplotContext,
  useTimeSeriesSensors,
} from 'features/timeSeries'
import { AgentBandOverlay } from 'features/timeSeries/agentTab/AgentBandOverlay'
import { ChartSaveAction } from 'features/timeSeries/components/chartActions/ChartSaveAction'
import { ExportPngButton } from 'features/timeSeries/components/chartActions/ExportPngButton'
import { MarkupToolButtons } from 'features/timeSeries/components/chartActions/MarkupToolButtons'
import { ChartTooltip } from 'features/timeSeries/components/ChartTooltip'
import { LabelConfigPanel } from 'features/timeSeries/components/labelTool/LabelConfigPanel'
import { LabelOverlay } from 'features/timeSeries/components/labelTool/LabelOverlay'
import { useLabelTool } from 'features/timeSeries/components/labelTool/useLabelTool'
import { PointAnnotationOverlay } from 'features/timeSeries/components/pointTool/PointAnnotationOverlay'
import { usePointAnnotationTool } from 'features/timeSeries/components/pointTool/usePointAnnotationTool'
import { useSeededMarkerAnnotation } from 'features/timeSeries/components/pointTool/useSeededMarkerAnnotation'
import { RulerOverlay } from 'features/timeSeries/components/rulerTool/RulerOverlay'
import { useRulerTool } from 'features/timeSeries/components/rulerTool/useRulerTool'
import { ThresholdConfigurationModal } from 'features/timeSeries/components/ThresholdConfigurationModal'
import { PenCapNotice } from 'features/timeSeries/components/timeSeriesScatterplot/PenCapNotice'
import { SplitWindowsOverlay } from 'features/timeSeries/components/windowsTool/SplitWindowsOverlay'
import { CHART_COLORS } from 'features/timeSeries/config/constants'
import { useCursorData } from 'features/timeSeries/CursorDataContext'
import { useInterpolatedHover } from 'features/timeSeries/hooks/useInterpolatedHover'
import { planPenAdditions } from 'features/timeSeries/utils/chartPenAdditions'
import {
  buildLoadSettledKey,
  getSettledLoadKey,
  resetLoadSettled,
  shouldAssertEmptyState,
  subscribeLoadSettled,
} from 'features/timeSeries/utils/loadSettledSignal'
import { shouldRenderPersistedOverlays } from 'features/timeSeries/utils/persistedOverlayVisibility'
import { useSensorTableData } from 'features/timeSeries/utils/scatterPlotTableHelpers'
import { Layers } from 'lucide-react'
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react'
import styled from 'styled-components'
import uPlot from 'uplot'

import { ChartLegend } from './ChartLegend'
import { MdDraftAlertCaseFlow } from './MdDraftAlertCaseFlow'
import { TimeSeriesScatterplotChart } from './TimeSeriesScatterplotChart'
import { useChartInteractionFunctions } from './useChartInteractionFunctions'
import { useChartViewportInteractions } from './useChartViewportInteractions'
import { useMemoizedSensorValues } from './useMemoizedSensorValues'
import { useUPlotGenerators } from './useUPlotGenerators'
import {
  buildYAxisOverlayLabels,
  getVisibleSensorsToRender,
  YAxisOverlayLabel,
} from './yAxisOverlayLabels'

const ChartSettingsButton = styled(CantileverButton)`
  position: absolute;
  top: 1rem;
  left: 1rem;
  z-index: 10;
`

const PopoverContent = styled.div`
  width: 24rem;
  max-height: 70vh;
  overflow-y: auto;
  padding: 1rem;
`

const ChartOverlayWrapper = styled.div`
  position: relative;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
`

const OverlayLabel = styled.button<{
  $color?: string
  $isInteractive: boolean
  $isSelected: boolean
}>`
  position: absolute;
  z-index: 5;
  background: rgba(0, 0, 0, 0.6);
  padding: 2px 4px;
  border-radius: 2px;
  border: none;
  font-size: 9px;
  font-family: Manrope, sans-serif;
  font-variant-numeric: tabular-nums;
  pointer-events: ${({ $isInteractive }) => ($isInteractive ? 'auto' : 'none')};
  color: ${({ $color }) => $color ?? '#ccc'};
  line-height: 1.2;
  cursor: ${({ $isInteractive }) => ($isInteractive ? 'ns-resize' : 'default')};
  user-select: none;
  box-shadow: ${({ $isSelected, $color }) =>
    $isSelected
      ? `inset 0 0 0 1px ${$color ?? '#ccc'}`
      : 'inset 0 0 0 1px transparent'};
  background: ${({ $isSelected }) =>
    $isSelected ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.6)'};

  &:hover {
    background: ${({ $isInteractive, $isSelected }) =>
      !$isInteractive
        ? $isSelected
          ? 'rgba(255, 255, 255, 0.12)'
          : 'rgba(0, 0, 0, 0.6)'
        : $isSelected
          ? 'rgba(255, 255, 255, 0.16)'
          : 'rgba(255, 255, 255, 0.08)'};
  }
`

const FloatingActionsContainer = styled.div`
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  z-index: 10;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  background: rgba(0, 0, 0, 0.6);
  border-radius: 0.25rem;
  padding: 0.125rem;
`

const MarkupToolsHud = styled.div`
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  z-index: 30;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  background: rgba(0, 0, 0, 0.6);
  border-radius: 0.25rem;
  padding: 0.125rem;
`

const BoxZoomSelection = styled.div`
  position: absolute;
  z-index: 12;
  pointer-events: none;
  border: 1px solid ${CHART_COLORS.CURSOR_ACCENT};
  background: rgba(122, 146, 255, 0.18);
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.14);
`

interface TimeSeriesScatterplotContentProps {
  showChartSettings?: boolean
  unitID?: string
  unitName?: string
  toggleChatbot?: () => void
  hideTooltip?: boolean
  initialManualAlertId?: string
  allowSavedAlertDelete?: boolean
  isInitializing?: boolean
  /** Hide the floating Save and Export PNG buttons (surfaces re-homing them
      in a toolbar). Saved-alert overlays stay mounted regardless. */
  hideFloatingActions?: boolean
  /** Hide the in-chart label/ruler/point HUD (surfaces re-homing it). */
  hideMarkupHud?: boolean
  /** Compact pen legend below the chart (surfaces without the pens rail). */
  showLegend?: boolean
}

export const TimeSeriesScatterplotContent = React.forwardRef<
  HTMLDivElement,
  TimeSeriesScatterplotContentProps
>(
  (
    {
      showChartSettings = true,
      unitID,
      unitName,
      hideTooltip,
      initialManualAlertId,
      allowSavedAlertDelete,
      isInitializing: isExternallyInitializing = false,
      hideFloatingActions = false,
      hideMarkupHud = false,
      showLegend = false,
    },
    ref
  ) => {
    const [chartSettingsOpen, setChartSettingsOpen] = useState(false)
    const [showThresholdModal, setShowThresholdModal] = useState(false)
    const [shouldAnimateSpinner, setShouldAnimateSpinner] = useState(false)
    const [showEmptyState, setShowEmptyState] = useState(false)
    const [chartSize, setChartSize] = useState({ width: 800, height: 400 })
    const initialSizeSet = useRef(false)
    const chartAreaRef = useRef<HTMLDivElement | null>(null)
    const uPlotRef = useRef<uPlot | null>(null)

    // Fetch and transform work orders into operational events
    const { operationalEvents } = useOperationalEvents(unitID)
    const { clearCursorData, updateCursorData, cursorData } = useCursorData()
    const {
      chartData,
      isLoading,
      isInitializing,
      derivedValues,
      unitMapping,
      sensorRanges,
      localThresholdOverrides,
      setLocalThresholdOverrides,
      connectPoints,
      xAxisSensorId,
      autoScaleMode,
      setAutoScaleMode,
      customAxisRanges,
      setCustomAxisRanges,
      setCustomXAxisRange,
      dataLayers,
      setDataLayers,
      setDateMode,
      setLiveTail,
      sensorVisibilityConfig,
      setSensorVisibilityConfig,
      customSensorColors,
      highlightedSensorId,
      setHighlightedSensorId,
      timeXAxisPreviewRange,
      setTimeXAxisPreviewRange,
      sensorEntries,
      selectedSensors,
      setSelectedSensors,
      siteID,
      singleScaleMode,
      sensorAxisMapping,
      timeRange,
      setTimeRange,
      loadTriggerRef,
      rulers,
      setRulers,
      pointAnnotations,
      setPointAnnotations,
      interactionMode,
      setInteractionMode,
      visibleLabelIds,
      setVisibleLabelIds,
      computedOverlay,
    } = useScatterplotContext()

    const tableData = useSensorTableData(
      selectedSensors,
      derivedValues,
      sensorRanges,
      customAxisRanges,
      cursorData,
      customSensorColors,
      xAxisSensorId,
      unitMapping,
      sensorEntries
    )

    const { enhancedSensorEntries, uniqueThresholds } = useTimeSeriesSensors({
      sensorEntries,
      selectedSensors,
      localThresholdOverrides,
    })

    const { isLiveTailRefresh, allSensorsToRender, selectedSensorEntries } =
      useMemoizedSensorValues({
        enhancedSensorEntries,
      })

    const visibleSensorsToRender = useMemo(
      () =>
        getVisibleSensorsToRender(allSensorsToRender, sensorVisibilityConfig),
      [allSensorsToRender, sensorVisibilityConfig]
    )

    const {
      handleUnhover,
      handleCursorMove,
      handleConfigureThresholds,
      handleSaveThresholdConfigurations,
      toggleLayer,
      cursorPosition,
      setCursorPosition,
    } = useChartInteractionFunctions({
      clearCursorData,
      allSensorsToRender,
      enhancedSensorEntries,
      updateCursorData,
      setShowThresholdModal,
      selectedSensors,
      unitID,
      unitName,
      siteID,
      setLocalThresholdOverrides,
      setDataLayers,
    })

    const onCursorLeave = useCallback(() => {
      handleUnhover()
    }, [handleUnhover])

    const { internalRef, handleMouseMove } = useInterpolatedHover({
      xAxisSensorId,
      connectPoints,
      chartData,
      allSensorsToRender,
      enhancedSensorEntries,
      unitMapping,
      updateCursorData,
      setCursorPosition,
      uPlotRef,
    })

    const {
      hoverState: rulerHoverState,
      draft: rulerDraft,
      isDragging: rulerIsDragging,
      resizingEdge: rulerResizingEdge,
      removeRuler,
      setRulerSensorIds,
      setRulerMinimized,
      setRulerPanelOffsetY,
      updateRulerPixels,
      handleOverlayMouseMove,
      handleOverlayMouseDown,
      handleEdgeMouseDown,
      handleOverlayMouseUp,
      handleOverlayMouseLeave,
      computeMetrics,
      getVisibleSensors,
    } = useRulerTool({
      uPlotRef,
      chartData,
      unitMapping,
      sensorRanges,
      allSensorsToRender,
      customSensorColors,
      isActive: interactionMode === 'ruler',
      rulers,
      setRulers,
    })

    const {
      removeAnnotation: removePointAnnotation,
      setAnnotationSensorId: setPointAnnotationSensorId,
      updateAnnotationAnchor: updatePointAnnotationAnchor,
      handleOverlayMouseMove: handlePointOverlayMouseMove,
      handleOverlayMouseDown: handlePointOverlayMouseDown,
      handleOverlayMouseLeave: handlePointOverlayMouseLeave,
      visibleSensors: pointVisibleSensors,
    } = usePointAnnotationTool({
      uPlotRef,
      allSensorsToRender,
      customSensorColors,
      isActive: interactionMode === 'point',
      annotations: pointAnnotations,
      setAnnotations: setPointAnnotations,
    })

    useSeededMarkerAnnotation({
      uPlotRef,
      marker: computedOverlay?.marker,
      dataRevision: chartData,
      setAnnotations: setPointAnnotations,
    })

    const {
      labels,
      draft: labelDraft,
      panel: labelPanel,
      editingLabelId,
      resizingEdge: labelResizingEdge,
      isSaving: isSavingLabel,
      getChartPens: getLabelChartPens,
      getRenderedRange: getLabelRenderedRange,
      openEditor: openLabelEditor,
      closeEditor: closeLabelEditor,
      cancelDraft: cancelLabelDraft,
      saveDraft: saveLabelDraft,
      saveEdits: saveLabelEdits,
      removeEditingLabel,
      handleOverlayMouseDown: handleLabelOverlayMouseDown,
      handleOverlayMouseMove: handleLabelOverlayMouseMove,
      handleOverlayMouseUp: handleLabelOverlayMouseUp,
      handleOverlayMouseLeave: handleLabelOverlayMouseLeave,
      handleEdgeMouseDown: handleLabelEdgeMouseDown,
    } = useLabelTool({
      uPlotRef,
      siteID,
      allSensorsToRender,
      customSensorColors,
      isActive: interactionMode === 'label',
      setVisibleLabelIds,
    })

    const visibleLabels = useMemo(
      () => labels.filter((label) => visibleLabelIds.includes(label.id)),
      [labels, visibleLabelIds]
    )

    const sensorEntriesById = useMemo(
      () => new Map(sensorEntries.map((sensor) => [sensor.id, sensor])),
      [sensorEntries]
    )

    const handleAddLabelPenToChart = useCallback(
      (sensorId: string) => {
        const { additions } = planPenAdditions(
          [sensorId],
          selectedSensors,
          sensorEntriesById
        )
        if (additions.length > 0) {
          setSelectedSensors((current) => [...current, ...additions])
        }
      },
      [selectedSensors, sensorEntriesById, setSelectedSensors]
    )

    // While a tool overlay is active it sits over the plot with
    // `pointer-events: all`, so mousemove lands on the overlay instead of the
    // chart area and the interpolated-hover updater never runs — leaving every
    // right-panel pen value stuck on "--". Forward the move to the hover updater
    // too so the readouts keep tracking the cursor while the tool is in use.
    const handleRulerOverlayMouseMove = useCallback(
      (e: React.MouseEvent<HTMLDivElement>) => {
        handleMouseMove(e)
        handleOverlayMouseMove(e)
      },
      [handleMouseMove, handleOverlayMouseMove]
    )
    const handleLabelOverlayMouseMoveWithHover = useCallback(
      (e: React.MouseEvent<HTMLDivElement>) => {
        handleMouseMove(e)
        handleLabelOverlayMouseMove(e)
      },
      [handleMouseMove, handleLabelOverlayMouseMove]
    )
    const handlePointOverlayMouseMoveWithHover = useCallback(
      (e: React.MouseEvent<HTMLDivElement>) => {
        handleMouseMove(e)
        handlePointOverlayMouseMove()
      },
      [handleMouseMove, handlePointOverlayMouseMove]
    )

    const visibleAnnotatedPointSensorIds = useMemo(() => {
      const fromMs = xAxisSensorId ? null : Date.parse(timeRange.from)
      const toMs = xAxisSensorId ? null : Date.parse(timeRange.to)

      return pointAnnotations.flatMap((annotation) => {
        if (annotation.sensorId === null) return []
        if (sensorVisibilityConfig[annotation.sensorId] === false) return []
        if (
          fromMs !== null &&
          toMs !== null &&
          (annotation.anchorTimestamp < fromMs ||
            annotation.anchorTimestamp > toMs)
        ) {
          return []
        }
        return [annotation.sensorId]
      })
    }, [
      pointAnnotations,
      sensorVisibilityConfig,
      timeRange.from,
      timeRange.to,
      xAxisSensorId,
    ])

    const visibleAnnotatedRulerSensorIds = useMemo(() => {
      if (rulers.length === 0) return []

      const fromMs = xAxisSensorId ? null : Date.parse(timeRange.from)
      const toMs = xAxisSensorId ? null : Date.parse(timeRange.to)

      const collected = new Set<string>()
      for (const ruler of rulers) {
        if (
          fromMs !== null &&
          toMs !== null &&
          (ruler.endXValue < fromMs || ruler.startXValue > toMs)
        ) {
          continue
        }
        for (const sensorId of ruler.selectedSensorIds) {
          if (sensorVisibilityConfig[sensorId] !== false) {
            collected.add(sensorId)
          }
        }
      }
      return Array.from(collected)
    }, [
      rulers,
      sensorVisibilityConfig,
      timeRange.from,
      timeRange.to,
      xAxisSensorId,
    ])

    const annotationProtectedSensorIds = useMemo(
      () =>
        Array.from(
          new Set([
            ...visibleAnnotatedPointSensorIds,
            ...visibleAnnotatedRulerSensorIds,
          ])
        ),
      [visibleAnnotatedPointSensorIds, visibleAnnotatedRulerSensorIds]
    )

    const {
      uPlotOptions,
      uPlotRecreateKey,
      uPlotSeriesAppearance,
      uPlotData,
      hasDataPoints,
    } = useUPlotGenerators({
      operationalEvents,
      enhancedSensorEntries,
      uniqueThresholds,
      allSensorsToRender,
      width: chartSize.width,
      height: chartSize.height,
      annotationProtectedSensorIds,
      onCursorMove: handleCursorMove,
      onCursorLeave,
    })

    const {
      boxZoomRect,
      handleAxisLabelDoubleClick,
      handleAxisLabelMouseDown,
      handleAxisLabelMouseEnter,
      handleAxisLabelMouseLeave,
      hoveredAxisId,
      isLabelDragging,
      previewXAxisRange,
      previewAxisRanges,
      selectedAxisIds,
    } = useChartViewportInteractions({
      enabled: interactionMode === 'none',
      autoScaleMode,
      customAxisRanges,
      setAutoScaleMode,
      setCustomAxisRanges,
      setCustomXAxisRange,
      setDateMode,
      setLiveTail,
      setTimeRange,
      chartData,
      loadTriggerRef,
      singleScaleMode,
      allSensorsToRender,
      timeRange,
      visibleSensorsToRender,
      sensorRanges,
      chartAreaRef,
      uPlotRef,
      uPlotRecreateKey,
      hasDataPoints,
      xAxisSensorId,
    })

    const chartDataTimeExtent = useMemo(() => {
      let min = Number.POSITIVE_INFINITY
      let max = Number.NEGATIVE_INFINITY

      chartData.forEach((point) => {
        const timestamp = point.timestamp
        if (typeof timestamp !== 'number' || Number.isNaN(timestamp)) {
          return
        }

        min = Math.min(min, timestamp)
        max = Math.max(max, timestamp)
      })

      if (!Number.isFinite(min) || !Number.isFinite(max)) {
        return null
      }

      return { min, max }
    }, [chartData])

    useEffect(() => {
      if (xAxisSensorId || !timeXAxisPreviewRange) {
        return
      }

      const committedMin = Date.parse(timeRange.from)
      const committedMax = Date.parse(timeRange.to)
      const timeRangeMatchesPreview =
        Number.isFinite(committedMin) &&
        Number.isFinite(committedMax) &&
        Math.abs(committedMin - timeXAxisPreviewRange.min) <= 1 &&
        Math.abs(committedMax - timeXAxisPreviewRange.max) <= 1

      if (!timeRangeMatchesPreview) {
        setTimeXAxisPreviewRange(null)
        return
      }

      if (
        chartDataTimeExtent &&
        chartDataTimeExtent.min >= timeXAxisPreviewRange.min - 1000 &&
        chartDataTimeExtent.max <= timeXAxisPreviewRange.max + 1000
      ) {
        setTimeXAxisPreviewRange(null)
      }
    }, [
      chartDataTimeExtent,
      setTimeXAxisPreviewRange,
      timeRange.from,
      timeRange.to,
      timeXAxisPreviewRange,
      xAxisSensorId,
    ])

    const highlightedAxisIds = useMemo(() => {
      if (isLabelDragging) {
        return selectedAxisIds
      }

      if (!hoveredAxisId) {
        return []
      }

      if (
        selectedAxisIds.length > 1 &&
        selectedAxisIds.includes(hoveredAxisId)
      ) {
        return selectedAxisIds
      }

      return [hoveredAxisId]
    }, [hoveredAxisId, isLabelDragging, selectedAxisIds])

    const highlightedSensorIdsFromAxisLabels = useMemo(
      () =>
        Array.from(
          new Set(
            highlightedAxisIds.flatMap((axisId) =>
              axisId === 'single-scale'
                ? visibleSensorsToRender.map(({ id }) => id)
                : [axisId]
            )
          )
        ),
      [highlightedAxisIds, visibleSensorsToRender]
    )

    const effectiveUPlotSeriesAppearance = useMemo(() => {
      if (highlightedSensorIdsFromAxisLabels.length === 0) {
        return uPlotSeriesAppearance
      }

      const primarySensorId = highlightedSensorIdsFromAxisLabels[0]
      const secondarySensorIds = new Set(
        highlightedSensorIdsFromAxisLabels.slice(1)
      )
      const annotationProtectedSensorIdSet = new Set(
        annotationProtectedSensorIds
      )

      return uPlotOptions.series.map((series, index) => {
        const baseAppearance = uPlotSeriesAppearance[index] ?? {}
        const seriesConfig = series as uPlot.Series & {
          _isRegression?: boolean
          _sensorId?: string
        }

        if (
          index === 0 ||
          !seriesConfig._sensorId ||
          seriesConfig._isRegression
        ) {
          return baseAppearance
        }

        // Spread the base so appearance fields this override does not manage
        // (dash, points.show/size) survive — they are re-asserted onto the
        // live instance wholesale, so dropping them here would clear them.
        if (seriesConfig._sensorId === primarySensorId) {
          return {
            ...baseAppearance,
            alpha: 1,
            width: Math.max(baseAppearance.width ?? 0, 1.9),
          }
        }

        if (secondarySensorIds.has(seriesConfig._sensorId)) {
          return {
            ...baseAppearance,
            alpha: 0.72,
            width: Math.max(baseAppearance.width ?? 0, 1.45),
          }
        }

        if (annotationProtectedSensorIdSet.has(seriesConfig._sensorId)) {
          return {
            ...baseAppearance,
            alpha: 1,
          }
        }

        return {
          ...baseAppearance,
          alpha: 0.24,
        }
      })
    }, [
      annotationProtectedSensorIds,
      highlightedSensorIdsFromAxisLabels,
      uPlotOptions.series,
      uPlotSeriesAppearance,
    ])

    const effectiveUPlotOptions = useMemo(() => {
      const nextXAxisRange = previewXAxisRange ?? timeXAxisPreviewRange

      if (!nextXAxisRange) {
        return uPlotOptions
      }

      return {
        ...uPlotOptions,
        scales: {
          ...uPlotOptions.scales,
          x: {
            ...uPlotOptions.scales?.x,
            auto: false,
            range: [nextXAxisRange.min, nextXAxisRange.max] as [number, number],
          },
        },
      }
    }, [previewXAxisRange, timeXAxisPreviewRange, uPlotOptions])

    // The zoom preview x-window is applied to the LIVE instance in place, via
    // the `setScale` path in useUPlotChart (it reads the pinned x out of
    // `effectiveUPlotOptions.scales.x` above). It must NOT be baked into the
    // recreate key.
    //
    // It used to be: this predates the move-in-place viewport model, when a
    // range change meant a teardown. Under that model the preview suffix was
    // how the pin took effect. It is now actively harmful — every drag-zoom
    // flips the preview on (fetch starts) and off (fetch settles), so keying
    // the recreate on it tears the uPlot instance down and rebuilds it twice
    // per zoom. That teardown is what:
    //   - bricks a SECOND drag-zoom started while the first is still loading:
    //     the first fetch settling (preview -> null) destroys the instance out
    //     from under uPlot's in-progress native drag-select, so the mouseup
    //     lands on a dead instance and the chart wedges. This is the
    //     "mid-selection teardown" useUPlotChart's header comment promises to
    //     protect against — honoured now by simply not tearing down.
    //   - rebuilds the canvas mid-load off a possibly-mid-layout size measure,
    //     which is what leaves the freshly zoomed trend rendered clipped.
    // Keeping the key structural lets the pin move the scales in place instead.
    const effectiveUPlotRecreateKey = uPlotRecreateKey

    // Reads the live uPlot instance through a ref, so the lint rule cannot see
    // what this depends on. `uPlotRecreateKey` is the structural signature of
    // every series field read (sensorId, label, show, isRegression) and
    // `getLabelChartPens` closes over the pen colors; `labelPanel` is the sole
    // consumer, and recomputing on its transitions is what guarantees the ref
    // is populated by the time the value is used.
    const labelChartPens = useMemo(
      () => getLabelChartPens(),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [getLabelChartPens, effectiveUPlotRecreateKey, labelPanel]
    )

    const effectiveAxisRanges = useMemo(
      () => ({ ...customAxisRanges, ...previewAxisRanges }),
      [customAxisRanges, previewAxisRanges]
    )

    /** Ymin/Ymax overlay labels derived from scale mode, ranges, and visibility */
    const yAxisOverlayLabels = useMemo((): YAxisOverlayLabel[] => {
      return buildYAxisOverlayLabels({
        visibleSensorsToRender,
        singleScaleMode,
        sensorRanges,
        customAxisRanges: effectiveAxisRanges,
        sensorAxisMapping,
        customSensorColors,
      })
    }, [
      visibleSensorsToRender,
      singleScaleMode,
      sensorRanges,
      effectiveAxisRanges,
      sensorAxisMapping,
      customSensorColors,
    ])

    useEffect(() => {
      if (interactionMode !== 'point' || !xAxisSensorId) return
      setInteractionMode('none')
    }, [interactionMode, xAxisSensorId, setInteractionMode])

    useEffect(() => {
      if (interactionMode === 'none') return
      // With a label config panel open, Esc belongs to the panel (it discards
      // the draft / abandons the edit) rather than to the tool.
      if (labelPanel !== null) return

      const handleKeyDown = (event: KeyboardEvent) => {
        if (event.key === 'Escape') {
          setInteractionMode('none')
        }
      }

      window.addEventListener('keydown', handleKeyDown)
      return () => window.removeEventListener('keydown', handleKeyDown)
    }, [interactionMode, labelPanel, setInteractionMode])

    // Capture initial chart area size for uPlot creation.
    // Subsequent resizes are handled by the chart's own ResizeObserver via setSize().
    useEffect(() => {
      if (!chartAreaRef.current) return

      const observer = new ResizeObserver((entries) => {
        if (initialSizeSet.current) return
        for (const entry of entries) {
          const { width, height } = entry.contentRect
          if (width > 0 && height > 0) {
            initialSizeSet.current = true
            setChartSize({ width, height })
          }
        }
      })

      observer.observe(chartAreaRef.current)
      return () => observer.disconnect()
    }, [])

    // Keep animation running during fade-out transition
    // Show spinner if loading AND (not a live tail refresh OR it's the initial load with no data yet)
    const isChartLoading =
      isLoading || isInitializing || isExternallyInitializing
    const hasConfiguredData =
      selectedSensors.length > 0 || derivedValues.length > 0
    const isInitialLoad = chartData.length === 0 && hasConfiguredData
    const shouldShowSpinner =
      isChartLoading && (!isLiveTailRefresh || isInitialLoad)

    useEffect(() => {
      if (shouldShowSpinner) {
        setShouldAnimateSpinner(true)
      } else {
        const timer = setTimeout(() => {
          setShouldAnimateSpinner(false)
        }, 200)

        return () => clearTimeout(timer)
      }
    }, [shouldShowSpinner, setShouldAnimateSpinner])

    // Key of the load currently on screen: this pen set over this window. The
    // data layer publishes the same key once a fetch for it genuinely settles.
    const currentLoadKey = useMemo(
      () =>
        buildLoadSettledKey(
          selectedSensors.map((sensor) => sensor.id),
          timeRange.from,
          timeRange.to
        ),
      [selectedSensors, timeRange.from, timeRange.to]
    )
    const settledLoadKey = useSyncExternalStore(
      subscribeLoadSettled,
      getSettledLoadKey
    )

    // Failsafe unpin of the zoom preview x-window (symptom: a freshly zoomed
    // trend rendered clipped to a stale window).
    //
    // The extent-based clear above releases the pin once the loaded data falls
    // within it — the common case. But that check is a heuristic on the data's
    // x-extent, so a load whose points land even slightly outside the requested
    // window (bucket-edge alignment) can leave the pin stuck, and the trend
    // stays clamped to the narrower preview window instead of filling the plot.
    // The load-settled signal is authoritative: once the fetch for THIS window
    // (`currentLoadKey`) has genuinely settled, the data on screen already IS
    // the new-range data, so the pin has done its job and must come off. It can
    // never fire early — the settled key is retracted for the duration of every
    // load and only re-published on a terminal outcome for the current window.
    useEffect(() => {
      if (xAxisSensorId || !timeXAxisPreviewRange) return
      if (settledLoadKey !== null && settledLoadKey === currentLoadKey) {
        setTimeXAxisPreviewRange(null)
      }
    }, [
      currentLoadKey,
      settledLoadKey,
      setTimeXAxisPreviewRange,
      timeXAxisPreviewRange,
      xAxisSensorId,
    ])

    // Draw saved rulers/annotations for as long as there is data on screen to
    // anchor them to. During a pan/zoom load the previous window's data is kept
    // on screen, and the overlay tracks it (sliding with the pan tween, or held
    // at the pinned zoomed window), so keeping them drawn never blanks saved
    // content mid-load. Only a cold load with nothing plotted yet leaves them
    // undrawn, since there is nothing to project against.
    const arePersistedOverlaysVisible = shouldRenderPersistedOverlays({
      hasData: hasDataPoints,
    })

    // Forget the module-level settle on unmount so a later mount cannot inherit a
    // stale "settled" from a previous session and flash empty before its own
    // first load.
    useEffect(() => resetLoadSettled, [])

    // Show the empty state only once a load has actually SETTLED empty for the
    // window on screen — never on the transient loading gap a superseded cold-
    // load fetch leaves behind (that gap can outlast this debounce, which is why
    // absence-of-loading alone is not a safe signal). The debounce only smooths
    // the final hand-off; the settle gate is what prevents the flash.
    useEffect(() => {
      if (
        shouldAssertEmptyState({
          hasDataPoints,
          isChartLoading,
          currentLoadKey,
          settledLoadKey,
        })
      ) {
        const timer = setTimeout(() => {
          setShowEmptyState(true)
        }, 100)

        return () => clearTimeout(timer)
      } else {
        setShowEmptyState(false)
      }
    }, [
      hasDataPoints,
      isChartLoading,
      currentLoadKey,
      settledLoadKey,
      setShowEmptyState,
    ])

    // Auto-disable operational events layer when no events are available
    useEffect(() => {
      if (operationalEvents.length === 0) {
        setDataLayers((prev) => {
          const eventsLayer = prev.find((layer) => layer.id === 'events')
          if (eventsLayer?.enabled) {
            return prev.map((layer) =>
              layer.id === 'events' ? { ...layer, enabled: false } : layer
            )
          }
          return prev
        })
      }
    }, [operationalEvents.length, setDataLayers])

    const pointToolDisabledReason = xAxisSensorId
      ? t`Point annotations require time on the x-axis`
      : t`Point Annotation`
    const isPointToolDisabled = !!xAxisSensorId || !hasDataPoints

    return (
      <>
        <FloatingActionsContainer>
          {!hideFloatingActions && <ChartSaveAction appearance='hud' />}
          {/* Saved-alert overlays and deep-link focus stay mounted even when
              the floating buttons are re-homed to a toolbar. */}
          <MdDraftAlertCaseFlow
            uPlotRef={uPlotRef}
            chartAreaRef={chartAreaRef}
            initialManualAlertId={initialManualAlertId}
            allowSavedAlertDelete={allowSavedAlertDelete}
          />
          {!hideFloatingActions && (
            <ExportPngButton
              appearance='hud'
              getCanvas={() => uPlotRef.current?.ctx?.canvas}
            />
          )}
        </FloatingActionsContainer>
        <ChartOverlayWrapper>
          {!hideMarkupHud && (
            <MarkupToolsHud data-testid='markup-tools-hud'>
              <MarkupToolButtons
                appearance='hud'
                pointDisabled={isPointToolDisabled}
                pointDisabledReason={pointToolDisabledReason}
              />
            </MarkupToolsHud>
          )}
          {showChartSettings && (
            <Popover
              content={
                <PopoverContent>
                  <ChartControls
                    dataLayers={dataLayers}
                    onToggleLayer={toggleLayer}
                    operationalEventsCount={operationalEvents.length}
                    handleConfigureThresholds={handleConfigureThresholds}
                  />
                </PopoverContent>
              }
              trigger='click'
              open={chartSettingsOpen}
              onOpenChange={setChartSettingsOpen}
              placement='bottomLeft'
              styles={{ root: { width: '24rem' } }}>
              <ChartSettingsButton
                onClick={() => setChartSettingsOpen(!chartSettingsOpen)}>
                <Layers size={16} />
                <Trans>Data Layers</Trans>
              </ChartSettingsButton>
            </Popover>
          )}
          <PenCapNotice />
          {yAxisOverlayLabels.map((label) => (
            <ChartTooltip
              key={label.key}
              label={t`Drag to shift this axis vertically. Shift-select multiple axes to drag together.`}
              side='top'>
              <OverlayLabel
                type='button'
                $color={label.color}
                $isInteractive={interactionMode === 'none' && hasDataPoints}
                $isSelected={selectedAxisIds.includes(label.axisId)}
                onDoubleClick={(event) =>
                  handleAxisLabelDoubleClick(label, event)
                }
                onMouseEnter={() => handleAxisLabelMouseEnter(label.axisId)}
                onMouseLeave={handleAxisLabelMouseLeave}
                onMouseDown={(event) => handleAxisLabelMouseDown(label, event)}
                style={{
                  [label.position === 'top' ? 'top' : 'bottom']: '4px',
                  [label.side === 'right' ? 'right' : 'left']:
                    `${4 + label.offset}px`,
                }}>
                {label.value}
              </OverlayLabel>
            </ChartTooltip>
          ))}
          {boxZoomRect && (
            <BoxZoomSelection
              style={{
                left: boxZoomRect.left,
                top: boxZoomRect.top,
                width: boxZoomRect.width,
                height: boxZoomRect.height,
              }}
            />
          )}
          <TimeSeriesScatterplotChart
            ref={(node) => {
              chartAreaRef.current = node
              if (ref) {
                if (typeof ref === 'function') {
                  ref(node)
                } else {
                  ref.current = node
                }
              }
            }}
            cursorPosition={cursorPosition}
            derivedValues={derivedValues}
            handleMouseMove={handleMouseMove}
            handleMouseLeave={handleUnhover}
            hasDataPoints={hasDataPoints}
            internalRef={internalRef}
            isLoading={isChartLoading}
            uPlotOptions={effectiveUPlotOptions}
            uPlotRecreateKey={effectiveUPlotRecreateKey}
            uPlotSeriesAppearance={effectiveUPlotSeriesAppearance}
            uPlotData={uPlotData}
            uPlotRef={uPlotRef}
            selectedSensors={selectedSensors}
            sensorVisibilityConfig={sensorVisibilityConfig}
            shouldAnimateSpinner={shouldAnimateSpinner}
            shouldShowSpinner={shouldShowSpinner}
            showEmptyState={showEmptyState}
            tableData={tableData}
            hideTooltip={interactionMode !== 'none' || hideTooltip}
          />
          {(interactionMode === 'ruler' || rulers.length > 0) && (
            <RulerOverlay
              uPlotRef={uPlotRef}
              isActive={interactionMode === 'ruler'}
              showPersisted={arePersistedOverlaysVisible}
              hoverState={rulerHoverState}
              rulers={rulers}
              draft={rulerDraft}
              isDragging={rulerIsDragging}
              resizingEdge={rulerResizingEdge}
              onSetRulerSensorIds={setRulerSensorIds}
              onRemoveRuler={removeRuler}
              onSetRulerMinimized={setRulerMinimized}
              onSetRulerPanelOffsetY={setRulerPanelOffsetY}
              onUpdateRulerPixels={updateRulerPixels}
              computeMetrics={computeMetrics}
              getVisibleSensors={getVisibleSensors}
              handleEdgeMouseDown={handleEdgeMouseDown}
              onMouseMove={handleRulerOverlayMouseMove}
              onMouseDown={handleOverlayMouseDown}
              onMouseUp={handleOverlayMouseUp}
              onMouseLeave={() => {
                handleOverlayMouseLeave()
                handleUnhover()
              }}
            />
          )}
          <SplitWindowsOverlay uPlotRef={uPlotRef} />
          <AgentBandOverlay uPlotRef={uPlotRef} />
          {(interactionMode === 'label' || visibleLabels.length > 0) && (
            <LabelOverlay
              uPlotRef={uPlotRef}
              isActive={interactionMode === 'label'}
              showPersisted={arePersistedOverlaysVisible}
              labels={visibleLabels}
              getRenderedRange={getLabelRenderedRange}
              draft={labelDraft}
              editingLabelId={editingLabelId}
              resizingEdge={labelResizingEdge}
              onEditLabel={openLabelEditor}
              handleEdgeMouseDown={handleLabelEdgeMouseDown}
              onMouseMove={handleLabelOverlayMouseMoveWithHover}
              onMouseDown={handleLabelOverlayMouseDown}
              onMouseUp={handleLabelOverlayMouseUp}
              onMouseLeave={() => {
                handleLabelOverlayMouseLeave()
                handleUnhover()
              }}
            />
          )}
          {labelPanel && (
            <LabelConfigPanel
              panel={labelPanel}
              chartPens={labelChartPens}
              isSaving={isSavingLabel}
              onSave={(input) => {
                if (labelPanel.mode === 'create') void saveLabelDraft(input)
                else void saveLabelEdits(input)
              }}
              onCancel={
                labelPanel.mode === 'create'
                  ? cancelLabelDraft
                  : closeLabelEditor
              }
              onDelete={() => void removeEditingLabel()}
              onAddPenToChart={handleAddLabelPenToChart}
            />
          )}
          {(interactionMode === 'point' || pointAnnotations.length > 0) && (
            <PointAnnotationOverlay
              uPlotRef={uPlotRef}
              isActive={interactionMode === 'point'}
              showPersisted={arePersistedOverlaysVisible}
              visibleSensors={pointVisibleSensors}
              unitMapping={unitMapping}
              sensorRanges={sensorRanges}
              annotations={pointAnnotations}
              onRemoveAnnotation={removePointAnnotation}
              onSetAnnotationSensorId={setPointAnnotationSensorId}
              onUpdateAnnotationAnchor={updatePointAnnotationAnchor}
              onMouseMove={handlePointOverlayMouseMoveWithHover}
              onMouseDown={handlePointOverlayMouseDown}
              onMouseLeave={() => {
                handlePointOverlayMouseLeave()
                handleUnhover()
              }}
            />
          )}
        </ChartOverlayWrapper>
        {showLegend && (
          <ChartLegend
            allSensorsToRender={allSensorsToRender}
            enhancedSensorEntries={enhancedSensorEntries}
            derivedValues={derivedValues}
            sensorRanges={sensorRanges}
            sensorVisibilityConfig={sensorVisibilityConfig}
            setSensorVisibilityConfig={setSensorVisibilityConfig}
            customSensorColors={customSensorColors}
            highlightedSensorId={highlightedSensorId}
            setHighlightedSensorId={setHighlightedSensorId}
          />
        )}
        <ThresholdConfigurationModal
          open={showThresholdModal}
          onClose={() => setShowThresholdModal(false)}
          sensors={selectedSensorEntries}
          onSave={(thresholdConfigs) => {
            handleSaveThresholdConfigurations(thresholdConfigs)
          }}
        />
      </>
    )
  }
)

TimeSeriesScatterplotContent.displayName = 'TimeSeriesScatterplotContent'
